'use client';
import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Sparkles, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';

interface LoginData {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const demoAccounts = [
  { role: 'Seeker', email: 'seeker@miraclechain.io', password: 'MiracleSeeker2026' },
  { role: 'Admin', email: 'admin@miraclechain.io', password: 'ChainAdmin2026!' },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
      {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
    </button>
  );
}

export default function AuthForm() {
  const router = useRouter();
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginForm = useForm<LoginData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupData>({ defaultValues: { name: '', email: '', password: '', confirmPassword: '', terms: false } });

  const handleLogin = async (data: LoginData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password }
    await new Promise((res) => setTimeout(res, 1500));

    const validAccounts = demoAccounts.map(a => a.email);
    if (!validAccounts.includes(data.email)) {
      loginForm.setError('email', { message: 'Invalid credentials — use the demo accounts below to sign in' });
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    toast.success('Welcome back, Seeker. Your blocks await.');

    if (data.email === 'admin@miraclechain.io') {
      router.push('/admin-panel');
    } else {
      router.push('/');
    }
  };

  const handleSignup = async (data: SignupData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/register with { name, email, password }
    await new Promise((res) => setTimeout(res, 1800));
    setIsLoading(false);
    toast.success('Your seeker account has been created. Welcome to MiracleChain.');
    router.push('/');
  };

  const fillCredentials = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    setTab('login');
  };

  return (
    <div className="w-full max-w-md">
      {/* Mobile logo */}
      <div className="flex items-center gap-2 mb-8 lg:hidden">
        <AppLogo size={36} />
        <span className="font-display text-lg font-semibold gold-gradient-text tracking-wider">MiracleChain</span>
      </div>

      {/* Tab switcher */}
      <div className="flex rounded-xl p-1 mb-8 border border-border" style={{ background: 'rgba(12,12,22,0.8)' }}>
        {(['login', 'signup'] as const).map((t) => (
          <button
            key={`auth-tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              tab === t
                ? 'bg-primary/15 text-primary border border-primary/25 glow-gold-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      {/* Login form */}
      {tab === 'login' && (
        <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl font-bold text-foreground mb-1">Welcome back, Seeker</h2>
            <p className="text-sm text-muted-foreground">Sign in to access your blocks and miracle history.</p>
          </div>

          {/* Google auth stub */}
          <button type="button"
            onClick={() => toast.info('Google sign-in — connect your OAuth provider')}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted transition-all duration-200">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or sign in with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="login-email">Email Address</label>
            <input
              id="login-email"
              type="email"
              {...loginForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              })}
              placeholder="seeker@miraclechain.io"
              className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
            />
            {loginForm.formState.errors.email && (
              <p className="mt-1.5 text-xs text-danger">{loginForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-foreground" htmlFor="login-password">Password</label>
              <button type="button" className="text-xs text-accent hover:text-accent/80 transition-colors">Forgot password?</button>
            </div>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                {...loginForm.register('password', { required: 'Password is required' })}
                placeholder="••••••••••••"
                className="w-full px-4 py-3 pr-12 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="mt-1.5 text-xs text-danger">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="remember"
              type="checkbox"
              {...loginForm.register('remember')}
              className="w-4 h-4 rounded border-border bg-input accent-primary"
            />
            <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">Remember me for 30 days</label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3.5 rounded-xl font-display font-semibold text-sm transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: isLoading ? 'rgba(201,168,76,0.4)' : 'linear-gradient(135deg, #a07830 0%, #f0d080 50%, #c9a84c 100%)',
              color: '#0a0a14',
              boxShadow: isLoading ? 'none' : '0 0 20px rgba(201,168,76,0.3)',
            }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Entering the Chain...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sparkles size={16} />
                Sign In to MiracleChain
              </span>
            )}
          </button>

          <p className="text-center text-sm text-muted-foreground">
            No account yet?{' '}
            <button type="button" onClick={() => setTab('signup')} className="text-primary hover:text-primary/80 font-medium transition-colors">
              Create one free
            </button>
          </p>
        </form>
      )}

      {/* Signup form */}
      {tab === 'signup' && (
        <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-5 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl font-bold text-foreground mb-1">Begin Your Journey</h2>
            <p className="text-sm text-muted-foreground">Create your seeker account to claim your first miracle block.</p>
          </div>

          {/* Google auth stub */}
          <button type="button"
            onClick={() => toast.info('Google sign-up — connect your OAuth provider')}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted transition-all duration-200">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or create with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="signup-name">Full Name</label>
            <input
              id="signup-name"
              type="text"
              {...signupForm.register('name', { required: 'Your name is required' })}
              placeholder="Your full name"
              className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
            />
            {signupForm.formState.errors.name && (
              <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="signup-email">Email Address</label>
            <input
              id="signup-email"
              type="email"
              {...signupForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              })}
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
            />
            {signupForm.formState.errors.email && (
              <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="signup-password">Password</label>
            <p className="text-xs text-muted-foreground mb-2">Minimum 8 characters with at least one number</p>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                {...signupForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                })}
                placeholder="••••••••••••"
                className="w-full px-4 py-3 pr-12 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            {signupForm.formState.errors.password && (
              <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="signup-confirm">Confirm Password</label>
            <div className="relative">
              <input
                id="signup-confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                placeholder="••••••••••••"
                className="w-full px-4 py-3 pr-12 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
              />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            {signupForm.formState.errors.confirmPassword && (
              <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex items-start gap-2">
            <input
              id="terms"
              type="checkbox"
              {...signupForm.register('terms', { required: 'You must accept the terms to continue' })}
              className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
            />
            <label htmlFor="terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
              I agree to the{' '}
              <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
              {' '}and{' '}
              <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>.
              I understand that block purchases are non-refundable.
            </label>
          </div>
          {signupForm.formState.errors.terms && (
            <p className="text-xs text-danger">{signupForm.formState.errors.terms.message}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3.5 rounded-xl font-display font-semibold text-sm transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: isLoading ? 'rgba(201,168,76,0.4)' : 'linear-gradient(135deg, #a07830 0%, #f0d080 50%, #c9a84c 100%)',
              color: '#0a0a14',
              boxShadow: isLoading ? 'none' : '0 0 20px rgba(201,168,76,0.3)',
            }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Creating Your Seeker Account...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sparkles size={16} />
                Create Seeker Account — Free
              </span>
            )}
          </button>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button type="button" onClick={() => setTab('login')} className="text-primary hover:text-primary/80 font-medium transition-colors">
              Sign in
            </button>
          </p>
        </form>
      )}

      {/* Demo credentials box */}
      <div className="mt-8 rounded-xl border border-border overflow-hidden" style={{ background: 'rgba(12,12,22,0.8)' }}>
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Demo Accounts</span>
        </div>
        <div className="divide-y divide-border">
          {demoAccounts.map((account) => (
            <div key={`demo-${account.role}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-primary">{account.role}</span>
                </div>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-xs text-muted-foreground font-mono truncate">{account.email}</span>
                  <CopyButton text={account.email} />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-mono">{'•'.repeat(account.password.length)}</span>
                  <CopyButton text={account.password} />
                </div>
              </div>
              <button
                onClick={() => fillCredentials(account.email, account.password)}
                className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium text-primary border border-primary/30 hover:bg-primary/10 transition-all"
              >
                Use
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}